import React from 'react'
import { Link } from 'react-router-dom'
import { FormGroup, Label, FormFeedback } from 'reactstrap'
import _ from 'lodash'
import Loading from 'components/Loading/Loading'
import CsvDownloadLink from 'components/CsvDownloadLink'
import { TOOL_DESCRIPTION } from './constants'
import {
  FormSection,
  SubmitButton,
  TextareaWrapper,
  StyledTextarea,
  ResultsSection,
  StyledAutoTable,
  TemplateLinkWrapper,
} from './styles'

import { CreateAccessCodeResult, CreateAccessCodeFailure } from 'types'
import { TableColumn } from 'types/component'

interface AccessCodesProps {
  errors: { siteIds?: string }
  touched: { siteIds?: boolean }
  handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void
  isSubmitting: boolean
  isValid: boolean
  createdItems?: CreateAccessCodeResult[] | null
  failedItems?: CreateAccessCodeFailure[] | null
  isLoading: boolean
}

const getAllAccessCodes = (accessCodes: Record<string, string>): string => {
  const values = Object.values(accessCodes)
  return values.length > 0 ? values.join(', ') : ''
}

const createdColumns: TableColumn<CreateAccessCodeResult>[] = [
  {
    title: 'Site ID',
    fieldValue: row => (
      <Link to={`/css-admin/sites/${row.siteId}`}>{row.siteId}</Link>
    ),
    sort: true,
    sortValue: row => row.siteId,
  },
  {
    title: 'NsCustomerId',
    fieldValue: row => row.nsCustomerId ?? null,
    sort: true,
    sortValue: 'nsCustomerId',
  },
  {
    title: 'Site Name',
    fieldValue: row =>
      row.siteName ? (
        <Link to={`/css-admin/sites/${row.siteId}`}>{row.siteName}</Link>
      ) : null,
    sort: true,
    sortValue: 'siteName',
  },
  {
    title: 'Access Codes',
    fieldValue: row => getAllAccessCodes(row.accessCodes),
    sort: false,
  },
]

const failedColumns: TableColumn<CreateAccessCodeFailure>[] = [
  {
    title: 'Site ID',
    fieldValue: row => row.siteId ?? null,
    sort: true,
  },
  {
    title: 'Errors',
    fieldValue: row => row.errors.join(', ') ?? null,
    sort: true,
  },
]

const createdHeaders = [
  { label: 'Site ID', key: 'siteId' },
  { label: 'NsCustomerId', key: 'nsCustomerId' },
  { label: 'Site Name', key: 'siteName' },
  { label: 'Access Codes', key: 'accessCodes' },
]

const failedHeaders = [
  { label: 'Site ID', key: 'siteId' },
  { label: 'Errors', key: 'errors' },
]

const AccessCodes: React.FC<AccessCodesProps> = ({
  errors,
  touched,
  handleSubmit,
  isSubmitting,
  isValid,
  createdItems,
  failedItems,
  isLoading,
}) => {
  const validationError = _.get(errors, 'siteIds')
  const isInvalid = validationError && _.get(touched, 'siteIds')
  const isFormDisabled = !isValid || isSubmitting

  const createdCsvData = createdItems
    ? createdItems.map(result => ({
        siteId: result.siteId,
        nsCustomerId: result.nsCustomerId,
        siteName: result.siteName,
        accessCodes: getAllAccessCodes(result.accessCodes),
      }))
    : []

  const failedCsvData = failedItems
    ? failedItems.map(item => ({
        siteId: item.siteId,
        errors: item.errors.join(', '),
      }))
    : []

  const hasCreatedItems = createdItems && createdItems.length > 0
  const hasFailedItems = failedItems && failedItems.length > 0

  return (
    <>
      <Loading isLoading={isLoading} message="Creating access codes...." />
      <h3>Tool Description</h3>
      <p>{TOOL_DESCRIPTION}</p>
      <FormSection>
        <form onSubmit={handleSubmit}>
          <TextareaWrapper>
            <FormGroup>
              <Label for="siteIds">Site Ids</Label>
              <StyledTextarea
                component="textarea"
                name="siteIds"
                id="siteIds"
                rows={5}
                disabled={isSubmitting}
                className={isInvalid ? 'is-invalid' : ''}
              />
              <FormFeedback>{validationError}</FormFeedback>
            </FormGroup>
          </TextareaWrapper>
          <div>
            <SubmitButton type="submit" disabled={isFormDisabled}>
              Submit
            </SubmitButton>
          </div>
        </form>
      </FormSection>
      {hasCreatedItems && (
        <ResultsSection>
          <h4>Successfully Created Access Codes</h4>
          <TemplateLinkWrapper>
            <CsvDownloadLink
              autoClick={false}
              data={createdCsvData}
              filename={`accesscodes_created_${new Date().toISOString()}.csv`}
              headers={createdHeaders}
            >
              Download Created Results
            </CsvDownloadLink>
          </TemplateLinkWrapper>
          <StyledAutoTable
            borderless
            columns={createdColumns}
            headThemeLight
            rows={createdItems}
            sort
          />
        </ResultsSection>
      )}
      {hasFailedItems && (
        <ResultsSection>
          <h4>Failed Items</h4>
          <TemplateLinkWrapper>
            <CsvDownloadLink
              autoClick={false}
              data={failedCsvData}
              filename={`accesscodes_failed_${new Date().toISOString()}.csv`}
              headers={failedHeaders}
            >
              Download Failed Results
            </CsvDownloadLink>
          </TemplateLinkWrapper>
          <StyledAutoTable
            borderless
            columns={failedColumns}
            headThemeLight
            rows={failedItems}
          />
        </ResultsSection>
      )}
    </>
  )
}

export default AccessCodes
